import React, { useMemo } from "react";
import { FileText } from "lucide-react";
import {
  Button,
  DataTable,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import { RunLogItem, RunType, RunStatus } from "../../../adverity-logs/api";
import "./run-logs-table.styles.scss";

const RUN_TYPE_LABELS: Record<RunType, string> = {
  authorization_monitor: "Authorization Monitor",
  datastream_creation: "Datastream Creation",
  backup_fetch: "Backup Fetch",
  datastream_update: "Datastream Update",
  authorization_sync: "Authorization Sync",
};

interface RunLogsTableProps {
  runLogs: RunLogItem[];
  loading: boolean;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (size: number) => void;
  onShowLogs: (runId: string) => void;
  onShowErrors: (runId: string) => void;
}

const RunLogsTable: React.FC<RunLogsTableProps> = ({
  runLogs,
  loading,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onShowLogs,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  const getStatusClasses = (status: RunStatus): string => {
    switch (status) {
      case "running":
        return "bg-amber-100 text-amber-700 border border-amber-400";
      case "completed":
        return "bg-green-100 text-green-700 border border-green-400";
      case "failed":
        return "bg-red-100 text-red-700 border border-red-400";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch {
      return dateStr;
    }
  };

  const calculateDuration = (startedAt: string, completedAt: string | null) => {
    if (!completedAt) return "-";
    try {
      const start = new Date(startedAt).getTime();
      const end = new Date(completedAt).getTime();
      const durationMs = end - start;
      const seconds = Math.floor(durationMs / 1000);
      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    } catch {
      return "-";
    }
  };

  type RowWithSrNo = RunLogItem & { _srNo: number };
  const tableData = useMemo<RowWithSrNo[]>(
    () => runLogs.map((r, i) => ({ ...r, _srNo: page * rowsPerPage + i + 1 })),
    [runLogs, page, rowsPerPage]
  );

  const columns = useMemo((): TableColumn<RowWithSrNo>[] => [
    { key: "_srNo", header: "Sr No", accessor: (row) => row._srNo, width: "80px", render: (_, row) => <span className="text-gray-600">{row._srNo}</span> },
    { key: "run_type", header: "Run Type", accessor: (row) => row.run_type, width: "18%", render: (_, row) => <span className="font-medium text-gray-900">{RUN_TYPE_LABELS[row.run_type] || row.run_type}</span> },
    { key: "started_at", header: "Started At", accessor: (row) => row.started_at, width: "18%", render: (_, row) => <span className="text-gray-600">{formatDateTime(row.started_at)}</span> },
    { key: "completed_at", header: "Completed At", accessor: (row) => row.completed_at, width: "18%", render: (_, row) => <span className="text-gray-600">{formatDateTime(row.completed_at)}</span> },
    { key: "duration", header: "Duration", accessor: () => "", width: "100px", render: (_, row) => <span className="text-gray-600">{calculateDuration(row.started_at, row.completed_at)}</span> },
    {
      key: "status",
      header: "Status",
      accessor: (row) => row.status,
      width: "120px",
      align: "center",
      render: (_, row) => (
        <span
          className={`inline-block px-3 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${getStatusClasses(row.status)}`}
          title={row.status}
        >
          {row.status}
        </span>
      ),
    },
    {
      key: "logs",
      header: "Logs",
      accessor: () => "",
      width: "80px",
      align: "center",
      render: (_, row) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onShowLogs(row.run_id)}
                className="h-8 w-8 text-[#334238] hover:bg-[#dbf566]/20"
              >
                <FileText className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent><p>View Logs</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
  ], [onShowLogs]);

  return (
    <DataTable<RowWithSrNo>
      className="run-logs-table-wrapper flex-1 min-h-0 overflow-auto"
      maxHeight="calc(100vh - 200px)"
      columns={columns}
      data={tableData}
      loading={loading}
      emptyMessage="No run logs found."
      stickyHeader
      stickyFooter
      showActions={false}
      pagination={{
        currentPage: page + 1,
        totalPages,
        pageSize: rowsPerPage,
        totalItems: totalCount,
        onPageChange: (p) => onPageChange(p - 1),
        onPageSizeChange: onRowsPerPageChange,
        pageSizeOptions: [25, 50, 100],
      }}
    />
  );
};

export default RunLogsTable;
